import {useEffect, useState} from 'react';
import {Link} from 'react-router-dom';
import {Button, Spin, Table, Space, Popconfirm, notification} from 'antd';

import {PackagesService} from './PackagesService';
import Title from 'antd/es/typography/Title';
import Text from 'antd/es/typography/Text';
import {resolveI18nObject} from '../../utils';
import type {NewPackageListItem} from '@PikaDoApp/npm-pikado-logic/dist/modules/package/graphql/package.interface';
import {useTablePagination} from '../../hooks/useTablePagination';

const packagesService = new PackagesService();

export const PackagesTable = () => {
  const [packages, setPackages] = useState<NewPackageListItem[]>([]);
  const [isError, setIsError] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const {
    current,
    pageSize,
    total,
    setTotal,
    paginationConfig,
    paginationSelector,
  } = useTablePagination({itemName: 'packages', defaultPageSize: 100});

  useEffect(() => {
    setIsLoading(true);
    packagesService
      .getPackages(current, pageSize)
      .then(resp => {
        setIsLoading(false);
        // //console.log('getPackagesr response ->->', resp);
        setPackages(resp.items);
        setTotal(resp.total ?? resp.items.length);
      })
      .catch(error => {
        setIsError(true);
        setIsLoading(false);
        //console.log('Error ->', error);
      });
  }, [current, pageSize]);
  const onChange = (pagination: any, filters: any, sorter: any, extra: any) => {
    ////console.log('params', pagination, filters, sorter, extra);
  };

  const columns: any = [
    {
      title: 'Name',
      dataIndex: ['name_i18n'],
      render: (name_i18n: any, record: NewPackageListItem) => {
        return (
          <Link to={`/packages/${record._id}/edit`}>
            {name_i18n?.en || name_i18n?.ar || 'N/A'}
          </Link>
        );
      },
      sortDirections: ['ascend', 'descend', 'ascend'],
      sorter: (a: any, b: any) => {
        const aName = a.name_i18n?.en || a.name_i18n?.ar || '';
        const bName = b.name_i18n?.en || b.name_i18n?.ar || '';
        return aName.localeCompare(bName);
      },
    },
    {
      title: 'Package Platform Type',
      dataIndex: ['type'],
      render: (type: string) => <Text>{type || 'STUDENT'}</Text>,
    },
    {
      title: 'Is free?',
      dataIndex: ['isFree'],
      render: (isFree: boolean) => (
        <Text style={{}}>{isFree ? 'Yes' : 'No'}</Text>
      ),
      sortDirections: ['ascend', 'descend', 'ascend'],
      sorter: (a: any, b: any) => (a.isFree ? 1 : 0) - (b.isFree ? 1 : 0),
    },
    {
      title: 'Only Ai?',
      dataIndex: ['onlyAI'],
      render: (onlyAI: boolean) => (
        <Text style={{}}>{onlyAI ? 'Yes' : 'No'}</Text>
      ),
      sortDirections: ['ascend', 'descend', 'ascend'],
      sorter: (a: any, b: any) => (a.onlyAI ? 1 : 0) - (b.onlyAI ? 1 : 0),
    },
    {
      title: 'Is Active?',
      dataIndex: ['isActive'],
      render: (isActive: boolean) => (
        <Text style={{}}>{isActive ? 'Yes' : 'No'}</Text>
      ),
      sortDirections: ['ascend', 'descend', 'ascend'],
      sorter: (a: any, b: any) => (a.isActive ? 1 : 0) - (b.isActive ? 1 : 0),
    },
    {
      title: 'Program ID',
      dataIndex: 'programId',
      render: (programId: string) => <Text>{programId}</Text>,
    },
    {
      title: 'ID',
      dataIndex: '_id',
      sorter: (a: any, b: any) => a._id.length - b._id.length,
      sortDirections: ['ascend', 'descend', 'ascend'],
      render: (id: string) => <Link to={`/packages/${id}/edit`}>{id}</Link>,
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: any) => (
        <Popconfirm
          title="Delete this package?"
          onConfirm={() => {
            packagesService
              .deletePackage(record._id || record.id)
              .then(() => {
                notification.success({message: 'Package deleted successfully'});
                setPackages(prev =>
                  prev.filter(
                    (p: any) => (p._id || p.id) !== (record._id || record.id),
                  ),
                );
                setTotal(prev => prev - 1);
              })
              .catch(e => {
                notification.error({
                  message: 'Failed to delete package',
                  description: e.message,
                });
              });
          }}>
          <Button danger type="link" style={{padding: 0}}>
            Delete
          </Button>
        </Popconfirm>
      ),
    },
  ];

  /**
   {
      title: 'Started at',
      dataIndex: ['startDate'],
      sortDirections: ['ascend', 'descend', 'ascend'],
      sorter: (a: any, b: any) =>
        moment(a.startDate).unix() - moment(b.startDate).unix(),
    },

   {
      title: 'Created at',
      dataIndex: ['createdAt'],
      defaultSortOrder: 'descend',
      sortDirections: ['ascend', 'descend', 'ascend'],
      sorter: (a: any, b: any) =>
        moment(a.createdAt).unix() - moment(b.createdAt).unix(),
    },
   {
      title: 'PlacementStatus',
      dataIndex: 'placementStatus',
      sortDirections: ['ascend', 'descend', 'ascend'],
      sorter: (a: any, b: any) => {
        return a.placementStatus.localeCompare(b.placementStatus);
      },
      render: (placementStatus: string) => (
        <p
          style={{
            color:
              placementStatus === 'DONE'
                ? 'green'
                : placementStatus === 'DOING'
                ? '#E2D410'
                : 'red',
          }}>
          {placementStatus}
        </p>
      ),
    },
   {
      title: 'PaymentStatus',
      key: 'paymentStatus',
      dataIndex: 'paymentStatus',
      sortDirections: ['ascend', 'descend', 'ascend'],
      sorter: (a: any, b: any) => {
        return a.paymentStatus.localeCompare(b.paymentStatus);
      },
      render: (paymentStatus: string) => (
        <p
          style={{
            color:
              paymentStatus === 'DONE'
                ? 'green'
                : paymentStatus === 'DOING'
                ? '#E2D410'
                : 'red',
          }}>
          {paymentStatus}
        </p>
      ),
    },
   {
      title: 'Student',
      dataIndex: ['student', 'name'],
      render: (studentName: string, record: Package) => <Text></Text>,
      sortDirections: ['ascend', 'descend', 'ascend'],
      sorter: (a: any, b: any) => {
        return a.student.name.localeCompare(b.student.name);
      },
    },
   */

  return (
    <>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          margin: 20,
        }}>
        <Title level={3} style={{margin: 0}}>
          Packages
          {!isLoading && total > 0 && (
            <Text
              type="secondary"
              style={{fontSize: 14, fontWeight: 400, marginLeft: 8}}>
              ({total} total)
            </Text>
          )}
        </Title>
        <Space>
          {paginationSelector}
          <Button type="primary" size={'large'}>
            <Link to={'/package/create'} style={{color: 'inherit'}}>
              Create
            </Link>
          </Button>
        </Space>
      </div>
      {!isError && !isLoading && (
        <Table
          pagination={paginationConfig}
          columns={columns}
          dataSource={packages}
          onChange={onChange}
        />
      )}
      {isLoading && (
        <Spin size="large" style={{margin: 'auto', width: '100%'}} />
      )}
      {isError && <Title>Packages fetching error ⚠️</Title>}
    </>
  );
};
